import {
  PROJECT_GUIDE_MICRO_STEP_DELAY_MS,
  type ProjectGuideEventCard,
  type ProjectGuideOperationReport,
  type ProjectGuideOperationScope,
  type ProjectGuideOperationSignal,
} from "@/components/project-guide/projectGuideMachine";
import { projectGuideOperationKey } from "@/components/project-guide/projectGuideMachine";
import { hasBlockingSecretsPolicy } from "@/components/project-guide/journeyStatus";
import { kindScopeForMessageTypes } from "@/pages/security/policy-scope";
import { useOrganization } from "@/contexts/Auth";
import { useFetcher } from "@/contexts/Fetcher";
import { useProjectSlugForRequests } from "@/contexts/Sdk";
import { downloadResponse } from "@/pages/plugins/downloadPluginPackage";
import { getRuleTitleFallback } from "@/pages/security/risk-utils";
import { useRoutes } from "@/routes";
import type { RiskPolicy } from "@gram/client/models/components/riskpolicy.js";
import type { RiskResult } from "@gram/client/models/components/riskresult.js";
import { useRiskCreatePolicyMutation } from "@gram/client/react-query/riskCreatePolicy.js";
import {
  invalidateRiskListPolicies,
  useRiskListPolicies,
} from "@gram/client/react-query/riskListPolicies.js";
import { useRiskListResults } from "@gram/client/react-query/riskListResults.js";
import { invalidateAllRiskListResults } from "@gram/client/react-query/riskListResults.js";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

export type SecretGuideClient = "claude" | "cursor" | "codex";

export const SECRET_GUIDE_CLIENTS: Record<
  SecretGuideClient,
  { installDirectory: string; label: string }
> = {
  claude: {
    installDirectory: "~/.claude/plugins/",
    label: "Claude Code",
  },
  codex: {
    installDirectory: "~/.codex/plugins/",
    label: "Codex",
  },
  cursor: {
    installDirectory: "~/.cursor/extensions/",
    label: "Cursor",
  },
};

const SECRET_GUIDE_SYNTHETIC_SECRET = "ghp_R2D2C3POLuk3Skywalker1234567890ab";
const SECRET_GUIDE_PROMPT = `Run this exact command in your shell:

echo "GITHUB_TOKEN=${SECRET_GUIDE_SYNTHETIC_SECRET}"`;

type ActiveOperation = {
  scope: ProjectGuideOperationScope;
  report: (report: ProjectGuideOperationReport) => void;
  paused: boolean;
};

// The secrets findings that existed when the guide started listening. A new
// finding for the guide's policy that matches the synthetic secret is the
// evidence; hook traces are not read because they need log:read.
type TelemetryBaseline = {
  capturedAtMs: number;
  syntheticSecretRedaction: string;
  resultIds: Set<string>;
};

function matchingSecretsPolicy(
  policies: RiskPolicy[] | undefined,
): RiskPolicy | undefined {
  return policies?.find((policy) => hasBlockingSecretsPolicy([policy]));
}

function responseFilename(response: Response, fallback: string): string {
  return (
    response.headers
      .get("Content-Disposition")
      ?.match(/filename="(.+?)"/)?.[1] ?? fallback
  );
}

function shellFilename(value: string): string {
  if (/^[A-Za-z0-9._-]+$/.test(value)) return value;
  return `'${value.replaceAll("'", `'"'"'`)}'`;
}

function installDetails(
  client: SecretGuideClient,
  filename: string | undefined,
): { command: string } | undefined {
  if (!filename) return undefined;
  if (client === "codex") {
    return {
      command: `unzip -oq ${shellFilename(filename)} -d ~/gram-observability\nbash ~/gram-observability/install.sh`,
    };
  }
  if (client === "claude") {
    return {
      command: `unzip -oq ${shellFilename(filename)} -d ~/gram-observability\nclaude --plugin-dir ~/gram-observability`,
    };
  }
  const { installDirectory } = SECRET_GUIDE_CLIENTS[client];
  return {
    command: `unzip -oq ${shellFilename(filename)} -d ${installDirectory}`,
  };
}

async function syntheticSecretRedaction(
  organizationId: string,
): Promise<string> {
  const encoder = new TextEncoder();
  const secret = encoder.encode(SECRET_GUIDE_SYNTHETIC_SECRET);
  const saltedSecret = encoder.encode(
    `${organizationId}\0${SECRET_GUIDE_SYNTHETIC_SECRET}`,
  );
  const digest = await globalThis.crypto.subtle.digest("SHA-256", saltedSecret);
  const sha = Array.from(new Uint8Array(digest).slice(0, 4), (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
  return `<redacted len=${secret.byteLength} sha=${sha}>`;
}

function matchesSyntheticSecret(
  result: RiskResult,
  expectedRedaction: string,
): boolean {
  if (!result.ruleId?.toLowerCase().includes("github")) return false;
  if (result.match !== undefined) {
    return result.match === SECRET_GUIDE_SYNTHETIC_SECRET;
  }
  return result.matchRedacted === expectedRedaction;
}

function newMatchingResults(
  results: RiskResult[] | undefined,
  baseline: TelemetryBaseline,
  policyId: string | undefined,
): RiskResult[] {
  return (
    results
      ?.filter(
        (result) =>
          !baseline.resultIds.has(result.id) &&
          result.createdAt.getTime() > baseline.capturedAtMs &&
          result.policyId === policyId &&
          result.source === "gitleaks" &&
          // Hook blocks record no outcome; a finding that recorded any other
          // outcome (logged, withheld, warned) was not a blocked request.
          (result.enforcementOutcome === undefined ||
            result.enforcementOutcome === "denied") &&
          matchesSyntheticSecret(result, baseline.syntheticSecretRedaction),
      )
      .sort(
        (left, right) => right.createdAt.getTime() - left.createdAt.getTime(),
      ) ?? []
  );
}

export function useSecretGuideOperations(): {
  baselineError: boolean;
  baselinePending: boolean;
  client: SecretGuideClient;
  clientSelected: boolean;
  downloadedFilename: string | undefined;
  handleSignal: (
    signal: ProjectGuideOperationSignal,
    report: (report: ProjectGuideOperationReport) => void,
  ) => void;
  installCommand: string | undefined;
  policyError: boolean;
  policyPending: boolean;
  promptCopied: boolean;
  prepareTelemetryBaseline: () => Promise<boolean>;
  prompt: string;
  retryPolicy: () => void;
  riskEventsHref: string;
  markPromptCopied: () => void;
  setClient: (client: SecretGuideClient) => void;
} {
  const gramProject = useProjectSlugForRequests();
  const organization = useOrganization();
  const { fetch: authFetch } = useFetcher();
  const routes = useRoutes();
  const queryClient = useQueryClient();
  const [client, setClientState] = useState<SecretGuideClient>();
  const [promptCopied, setPromptCopied] = useState(false);
  const [downloadedFilename, setDownloadedFilename] = useState<string>();
  const [createdPolicy, setCreatedPolicy] = useState<RiskPolicy>();
  const [activeOperation, setActiveOperation] = useState<ActiveOperation>();
  const activeOperationRef = useRef<ActiveOperation | undefined>(undefined);
  const [, setBaseline] = useState<TelemetryBaseline>();
  const baselineRef = useRef<TelemetryBaseline | undefined>(undefined);
  const baselineGenerationRef = useRef(0);
  const [baselineError, setBaselineError] = useState(false);
  const [baselinePending, setBaselinePending] = useState(false);
  const [suppressTelemetryError, setSuppressTelemetryError] = useState(false);
  const startedFor = useRef(new Set<string>());
  const downloadStartedFor = useRef<string | undefined>(undefined);
  const downloadTimerRef = useRef<{ key: string; timer: number } | undefined>(
    undefined,
  );

  const policiesQuery = useRiskListPolicies({ gramProject }, undefined, {
    throwOnError: false,
  });
  const matchingPolicy = matchingSecretsPolicy(policiesQuery.data?.policies);
  const policy = matchingPolicy ?? createdPolicy;
  const policyId = policy?.id;
  const policyPending = policiesQuery.isPending;
  const policyError =
    policiesQuery.isError ||
    (!policiesQuery.isPending && policiesQuery.data === undefined);
  const createPolicy = useRiskCreatePolicyMutation();

  const listening =
    activeOperation?.scope.step === 4 && !activeOperation.paused;
  const resultsQuery = useRiskListResults(
    { gramProject, category: "secrets", policyId, limit: 20 },
    undefined,
    {
      enabled: listening,
      refetchInterval: listening ? 2_000 : false,
      throwOnError: false,
    },
  );
  const telemetryQueryError = resultsQuery.isError;
  const telemetryError = baselineError || telemetryQueryError;

  const updateActiveOperation = useCallback(
    (operation: ActiveOperation | undefined) => {
      activeOperationRef.current = operation;
      setActiveOperation(operation);
    },
    [],
  );

  const isCurrentOperation = useCallback((key: string): boolean => {
    const current = activeOperationRef.current;
    return Boolean(current && projectGuideOperationKey(current.scope) === key);
  }, []);

  const captureBaseline = useCallback(async (): Promise<boolean> => {
    const generation = baselineGenerationRef.current + 1;
    baselineGenerationRef.current = generation;
    baselineRef.current = undefined;
    setBaseline(undefined);
    setBaselineError(false);
    setBaselinePending(true);
    setSuppressTelemetryError(true);
    try {
      const [results, expectedRedaction] = await Promise.all([
        resultsQuery.refetch(),
        syntheticSecretRedaction(organization.id),
      ]);
      if (baselineGenerationRef.current !== generation) return false;
      if (results.isError || !results.data) {
        setBaselineError(true);
        return false;
      }
      const nextBaseline = {
        capturedAtMs: Date.now(),
        syntheticSecretRedaction: expectedRedaction,
        resultIds: new Set(results.data.results.map((result) => result.id)),
      };
      baselineRef.current = nextBaseline;
      setBaseline(nextBaseline);
      return true;
    } catch {
      if (baselineGenerationRef.current !== generation) return false;
      setBaselineError(true);
      return false;
    } finally {
      if (baselineGenerationRef.current === generation) {
        setBaselinePending(false);
        setSuppressTelemetryError(false);
      }
    }
  }, [organization.id, resultsQuery]);

  const retryTelemetry = useCallback(() => {
    setSuppressTelemetryError(true);
    void resultsQuery.refetch().finally(() => setSuppressTelemetryError(false));
  }, [resultsQuery]);

  const handleSignal = useCallback(
    (
      signal: ProjectGuideOperationSignal,
      report: (report: ProjectGuideOperationReport) => void,
    ): void => {
      if (signal.scope.path !== "secret-block") return;
      if (signal.type === "abort") {
        updateActiveOperation(undefined);
        baselineGenerationRef.current += 1;
        baselineRef.current = undefined;
        setBaseline(undefined);
        setBaselinePending(false);
        setSuppressTelemetryError(false);
        return;
      }
      if (signal.type === "pause") {
        const current = activeOperationRef.current;
        if (
          current &&
          projectGuideOperationKey(current.scope) ===
            projectGuideOperationKey(signal.scope)
        ) {
          updateActiveOperation({ ...current, paused: true });
        }
        return;
      }
      if (signal.type === "checkpoint") return;
      updateActiveOperation({ scope: signal.scope, report, paused: false });
      if (signal.type === "start" && signal.scope.step === 4) {
        report({
          type: "progress",
          scope: signal.scope,
          message:
            "Waiting for a new blocked hook and matching secrets risk event.",
        });
      }
      if (signal.type === "retry" && signal.scope.step === 4) {
        if (baselineRef.current) {
          retryTelemetry();
        } else {
          void captureBaseline();
        }
      }
    },
    [captureBaseline, retryTelemetry, updateActiveOperation],
  );

  useEffect(() => {
    const operation = activeOperation;
    if (!operation || operation.paused || operation.scope.step !== 0) return;
    if (policyPending) return;
    if (policyError) {
      updateActiveOperation(undefined);
      operation.report({
        type: "error",
        scope: operation.scope,
        message:
          "Could not read this project's risk policies. Retry the policy check.",
      });
      return;
    }
    if (matchingPolicy || createdPolicy) {
      updateActiveOperation(undefined);
      operation.report({
        type: "success",
        scope: operation.scope,
        result: "Secrets policy already live · block on match",
      });
      return;
    }
    const key = projectGuideOperationKey(operation.scope);
    if (startedFor.current.has(key)) return;
    startedFor.current.add(key);
    for (const [message, progress] of [
      ["Detecting and enabling the Secrets category", 0.25],
      ["Scoping user prompts", 0.5],
      ["Setting the action to deny", 0.75],
    ] as const) {
      operation.report({
        type: "progress",
        scope: operation.scope,
        message,
        progress,
      });
    }
    void createPolicy
      .mutateAsync({
        request: {
          gramProject,
          createRiskPolicyRequestBody: {
            action: "block",
            audienceType: "everyone",
            autoName: true,
            enabled: true,
            detectionScopes: [
              {
                category: "secrets",
                ...kindScopeForMessageTypes(["tool_request", "tool_response"]),
              },
            ],
            policyType: "standard",
            sources: ["gitleaks"],
          },
        },
      })
      .then((policy) => {
        if (!isCurrentOperation(key)) return;
        updateActiveOperation(undefined);
        setCreatedPolicy(policy);
        void invalidateRiskListPolicies(queryClient, [{ gramProject }]);
        operation.report({
          type: "success",
          scope: operation.scope,
          result: "Secrets policy created · block on match",
        });
      })
      .catch(() => {
        if (!isCurrentOperation(key)) return;
        updateActiveOperation(undefined);
        operation.report({
          type: "error",
          scope: operation.scope,
          message:
            "Could not create the blocking secrets policy. Retry the policy step.",
        });
      });
  }, [
    activeOperation,
    createPolicy,
    createdPolicy,
    gramProject,
    matchingPolicy,
    policyError,
    policyPending,
    queryClient,
    isCurrentOperation,
    updateActiveOperation,
  ]);

  useEffect(() => {
    const operation = activeOperation;
    if (!operation || operation.paused || operation.scope.step !== 1) return;
    if (!client) {
      updateActiveOperation(undefined);
      return;
    }
    if (downloadedFilename) {
      updateActiveOperation(undefined);
      operation.report({
        type: "success",
        scope: operation.scope,
        result: `Observability plugin downloaded · ${downloadedFilename}`,
      });
      return;
    }
    const key = projectGuideOperationKey(operation.scope);
    if (!startedFor.current.has(key)) {
      startedFor.current.add(key);
      for (const [message, progress] of [
        [
          `Building the ${SECRET_GUIDE_CLIENTS[client].label} observability plugin`,
          0.25,
        ],
        ["Signing the observability plugin bundle", 0.5],
      ] as const) {
        operation.report({
          type: "progress",
          scope: operation.scope,
          message,
          progress,
        });
      }
    }
    if (downloadStartedFor.current === key) return;
    if (downloadTimerRef.current?.key === key) return;

    const timer = window.setTimeout(() => {
      downloadTimerRef.current = undefined;
      const current = activeOperationRef.current;
      if (
        !current ||
        current.paused ||
        projectGuideOperationKey(current.scope) !== key
      ) {
        return;
      }
      downloadStartedFor.current = key;
      void authFetch(
        `/rpc/plugins.downloadObservabilityPlugin?platform=${client}`,
        {},
      )
        .then(async (response) => {
          if (!response.ok) throw new Error("download failed");
          const fallback = `observability-${client}.zip`;
          const filename = responseFilename(response, fallback);
          await downloadResponse(response, fallback);
          if (!isCurrentOperation(key)) return;
          setDownloadedFilename(filename);
          updateActiveOperation(undefined);
          operation.report({
            type: "success",
            scope: operation.scope,
            result: `Observability plugin downloaded · ${filename}`,
          });
        })
        .catch(() => {
          if (!isCurrentOperation(key)) return;
          updateActiveOperation(undefined);
          operation.report({
            type: "error",
            scope: operation.scope,
            message:
              "Could not download the observability plugin. Retry the download step.",
          });
        });
    }, PROJECT_GUIDE_MICRO_STEP_DELAY_MS * 2);
    downloadTimerRef.current = { key, timer };
    return () => {
      if (downloadTimerRef.current?.timer !== timer) return;
      window.clearTimeout(timer);
      downloadTimerRef.current = undefined;
    };
  }, [
    activeOperation,
    authFetch,
    client,
    downloadedFilename,
    isCurrentOperation,
    updateActiveOperation,
  ]);

  useEffect(() => {
    const operation = activeOperation;
    if (!operation || operation.paused || operation.scope.step !== 4) return;
    const currentBaseline = baselineRef.current;
    if (suppressTelemetryError) {
      return;
    }
    if (!currentBaseline) {
      return;
    }
    if (resultsQuery.isPending) {
      return;
    }
    if (telemetryError) {
      updateActiveOperation(undefined);
      operation.report({
        type: "error",
        scope: operation.scope,
        message:
          "Could not check for the new blocked event. Retry after checking the plugin connection.",
      });
      return;
    }
    const riskResult = newMatchingResults(
      resultsQuery.data?.results,
      currentBaseline,
      policy?.id,
    )[0];
    if (!riskResult) return;
    const event: ProjectGuideEventCard = {
      kind: "Denied · risk event",
      tone: "deny",
      title: "request denied by secrets policy",
      rows: [
        { key: "rule", value: getRuleTitleFallback(riskResult.ruleId) },
        { key: "match", value: "synthetic credential" },
      ],
      note: "The prompt was blocked before the model answered.",
    };
    updateActiveOperation(undefined);
    void invalidateAllRiskListResults(queryClient);
    operation.report({ type: "event", scope: operation.scope, event });
  }, [
    activeOperation,
    policy,
    resultsQuery.data?.results,
    resultsQuery.isPending,
    suppressTelemetryError,
    telemetryError,
    updateActiveOperation,
    gramProject,
    queryClient,
  ]);

  const resolvedClient = client ?? "claude";
  const install = installDetails(resolvedClient, downloadedFilename);

  return {
    baselineError,
    baselinePending,
    client: resolvedClient,
    clientSelected: client !== undefined,
    downloadedFilename,
    handleSignal,
    installCommand: install?.command,
    policyError,
    policyPending,
    promptCopied,
    prepareTelemetryBaseline: captureBaseline,
    prompt: SECRET_GUIDE_PROMPT,
    retryPolicy: () => {
      void policiesQuery.refetch();
    },
    riskEventsHref: routes.riskEvents.href(),
    markPromptCopied: () => setPromptCopied(true),
    setClient: (nextClient) => {
      if (downloadedFilename) return;
      setClientState(nextClient);
      setPromptCopied(false);
    },
  };
}
